import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { changelang } from "./funtionsBD/Mainservice";
import {
  APP_LANGUAGES,
  appLanguageMeta,
  applyAppLanguage,
  currentAppLanguage,
} from "../utils/appLanguages";
import { markInviteUiLangOverride } from "../utils/clubCountryLanguage";
import "./LanguageShortcutFab.css";

const HIDDEN_PATHS = new Set(["/live-training", "/ChangeLanguage", "/demo"]);

/**
 * @param {{ variant?: "fab" | "inline" | "invite", onPicked?: (lng: string) => void }} props
 * - fab: fixed bottom-left (legacy; prefer not mounting globally)
 * - inline: compact control for admin/coach top bar
 * - invite: compact control beside the join-club banner
 */
export function LanguageShortcutFab({ variant = "fab", onPicked }) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const rootRef = useRef(null);
  const panelRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(null);
  const selected = currentAppLanguage(i18n);
  const current = appLanguageMeta(selected);
  const isInline = variant === "inline" || variant === "invite";

  const hidden =
    !isInline &&
    (HIDDEN_PATHS.has(location.pathname) ||
      location.pathname.startsWith("/live-training"));

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    const onPointer = (e) => {
      if (!rootRef.current?.contains(e.target)) close();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("touchstart", onPointer, { passive: true });
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("touchstart", onPointer);
    };
  }, [open, close]);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useLayoutEffect(() => {
    if (!open || !isInline) return undefined;
    const panel = panelRef.current;
    const root = rootRef.current;
    if (!panel || !root) return undefined;

    const clampToViewport = () => {
      const margin = 8;
      const rect = root.getBoundingClientRect();
      const width = panel.offsetWidth;
      const maxRight = window.innerWidth - margin;
      let left = rect.left;
      if (left + width > maxRight) {
        left = maxRight - width;
      }
      if (left < margin) {
        left = margin;
      }
      panel.style.left = `${Math.round(left - rect.left)}px`;
      panel.style.right = "auto";
    };

    clampToViewport();
    window.addEventListener("resize", clampToViewport);
    return () => {
      window.removeEventListener("resize", clampToViewport);
      panel.style.left = "";
      panel.style.right = "";
    };
  }, [open, isInline]);

  const changeLanguage = async (lng) => {
    if (variant === "invite") {
      markInviteUiLangOverride(lng);
    }
    if (lng === selected && !pending) {
      onPicked?.(lng);
      close();
      return;
    }
    setPending(lng);
    applyAppLanguage(i18n, lng);
    onPicked?.(lng);
    try {
      if (localStorage.getItem("token")) {
        await changelang(lng);
      }
    } catch (error) {
      console.warn(
        "changeLang API failed; UI language still applied:",
        error?.message || error
      );
    } finally {
      setPending(null);
      close();
    }
  };

  if (hidden) return null;

  return (
    <div
      ref={rootRef}
      className={`lang-shortcut${isInline ? " lang-shortcut--inline" : ""}${
        variant === "invite" ? " lang-shortcut--invite" : ""
      }`}
    >
      {open ? (
        <div
          ref={panelRef}
          className="lang-shortcut__panel"
          role="listbox"
          aria-label={t("changelanguage")}
        >
          {APP_LANGUAGES.map((lang) => {
            const isSelected = selected === lang.code;
            const isPending = pending === lang.code;
            return (
              <button
                key={lang.code}
                type="button"
                role="option"
                aria-selected={isSelected}
                className={`lang-shortcut__option${isSelected ? " is-selected" : ""}${
                  isPending ? " is-pending" : ""
                }`}
                onClick={() => changeLanguage(lang.code)}
                disabled={Boolean(pending) && !isPending}
              >
                <span className="lang-shortcut__flag" aria-hidden="true">
                  {lang.flag}
                </span>
                <span className="lang-shortcut__meta">
                  <span className="lang-shortcut__name">{lang.nativeName}</span>
                  <span className="lang-shortcut__code">{lang.code.toUpperCase()}</span>
                </span>
                {isSelected ? (
                  <span className="lang-shortcut__check" aria-hidden="true">
                    ✓
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      ) : null}
      <button
        type="button"
        className={`lang-shortcut__btn${open ? " is-open" : ""}`}
        onClick={() => setOpen((v) => !v)}
        aria-label={t("changelanguage")}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={t("changelanguage")}
      >
        <span className="lang-shortcut__btn-flag" aria-hidden="true">
          {current.flag}
        </span>
        <span className="lang-shortcut__btn-code">{current.code.toUpperCase()}</span>
      </button>
    </div>
  );
}

export default LanguageShortcutFab;
